/** @jsx jsx */
import React from 'react';
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import { Formik } from 'formik';
import { object, string } from 'yup';
import axios from 'axios';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { withNamespaces } from 'react-i18next';
import defaultsDeep from 'lodash/defaultsDeep';
import get from 'lodash/get';
import compose from 'lodash/fp/compose';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import { Input, Checkbox, PhoneNumber } from '../../../ui/Form/index';
import { Icon } from '../../../ui/Icon';
import Picture from '../../../ui/Picture';
import createButtonCss from '../../../ui/Button';
import CtaLink from '../../../ui/CtaLink';
import { setDataLayerObj } from '../../../../utils/analytics';
import {
  API,
  CLOUDINARY_BASE_VALUES,
  REG_ONLY_NUMBERS,
  COMPONENT_NAMES,
  SOLUTIONS,
} from '../../../../utils/constants';
import { setAntiForgeryToken } from '../../../../utils/forms';
import { FORM_COPY as dict } from '../../../../utils/dictionarykeys';
import getComponent from '../../../../utils/getComponent';
import { decorateRequiredPlaceholder } from '../../Enquiry';
import { exitBuilder } from '../../SolutionBuilderNav';
import { getContactLineDataFromContext } from '../../ContactLineNumber';
import { Breakpoints } from '../../../../styles/Grid/GridSizes';
import * as styles from './index.styles.js';

const FORM_ID = 'solution_builder_form';

const enquiryDefaultData = {
  AntiForgery: {
    name: '',
    value: '',
  },
  ContactPhrase: {
    value: '',
  },
  ContactIcon: {},
  DomainCountryISOCode2: {
    value: '',
  },
  LocalPhoneNumber: {
    value: '',
  },
  PhoneNumberOptions: [
    {
      fields: {
        CountryName: {
          value: '',
        },
        DialingCode: {
          value: '',
        },
        Flag: {
          value: '',
        },
        CountryISOCode3: {
          value: '',
        },
        CountryISOCode2: {
          value: '',
        },
      },
    },
  ],
  PrivacyPolicyLink: {
    value: {
      href: '',
      text: '',
    },
  },
};

const solutionBuilderDefaultData = {
  SidePanelFormHeadline: {
    value: '',
  },
  SidePanelFormThankYouHeadline: {
    value: '',
  },
  SidePanelFormThankYouText: {
    value: '',
  },
};

const contactImageSize = {
  ...CLOUDINARY_BASE_VALUES,
  aspectRatio: '1:1',
  height: 48,
  width: 48,
};

const contactImageSizes = {
  [Breakpoints.sm]: contactImageSize,
  [Breakpoints.md]: contactImageSize,
  [Breakpoints.lg]: contactImageSize,
  [Breakpoints.xl]: contactImageSize,
};

class SidePanelEnquiryForm extends SafePureComponent {
  contactLineData = getContactLineDataFromContext(this.props.sitecoreContext);
  enquiryComponent = getComponent(
    this.props.sitecoreContext,
    COMPONENT_NAMES.enquiryModal,
  );
  enquiryFields = defaultsDeep(
    get(this.enquiryComponent, 'fields', {}),
    enquiryDefaultData,
  );
  solutionBuilderResultsComponent = getComponent(
    this.props.sitecoreContext,
    COMPONENT_NAMES.solutionBuilderResults,
  );
  solutionBuilderResultsFields = defaultsDeep(
    get(this.solutionBuilderResultsComponent, 'fields', {}),
    solutionBuilderDefaultData,
  );
  phoneNumberRef = React.createRef();
  state = {
    isThankyouVisible: false,
  };

  getPhoneParams(values) {
    const countryISOCode2 = get(
      this.phoneNumberRef,
      'current.state.countryData.CountryISOCode2',
      {},
    );
    return `countryISOCode2=${countryISOCode2}&phoneNumber=${values.PhoneNumber}`;
  }

  renderGeneralError(msg) {
    return (
      <div css={styles.generalError}>
        <Icon name="error" customCss={styles.errorIcon} />
        {msg}
      </div>
    );
  }

  trackFormSubmitSuccess({ formName, id }) {
    setDataLayerObj({
      event: 'SBcontactFormResponseObject',
      formName,
      id,
      submitButton: 'submit',
    });
  }

  trackFormSubmitError(error) {
    setDataLayerObj({
      event: 'SBcontactFormFailedSubmission',
      contactFormError: error.message,
      formId: FORM_ID,
    });
  }

  renderPhoneNumberBlock() {
    const { ContactIcon, ContactPhrase } = this.enquiryFields;
    const phone = get(this.contactLineData, 'fields.CountryPhone.value', '');

    return (
      <div css={styles.contactNumberBlock}>
        <div css={styles.contactNumberImageContainer}>
          <Picture
            imageCss={styles.contactNumberImage}
            image={ContactIcon}
            imageSizes={contactImageSizes}
            instantLoad
          />
        </div>
        <div>
          <h4 css={styles.contactNumberTitle}>{get(ContactPhrase, 'value')}</h4>
          <a href={`tel: ${phone}`} css={styles.contactNumberLink}>
            {phone}
          </a>
        </div>
      </div>
    );
  }

  renderFormHeadline() {
    return (
      <h1 css={styles.headline}>
        {this.solutionBuilderResultsFields.SidePanelFormHeadline.value}
      </h1>
    );
  }

  mapValues(values) {
    const { DomainCountryISOCode2 } = this.enquiryFields;
    const { LocationName, ProtonId } = this.props.location;
    const countryData = get(
      this.phoneNumberRef,
      'current.state.countryData',
      {},
    );

    return {
      ...values,
      ...countryData,
      /**
       * Current Url is needed since here we have the values for the solution builder results,
       * requested to be sent in the comments input.
       */
      Comments: window.location.href,
      Solution: SOLUTIONS.OFFICE_SPACE_ID,
      DomainCountryISOCode2: DomainCountryISOCode2.value,
      LocationID: ProtonId.toString(),
      LocationName,
    };
  }

  onFormSubmit = ({ formName }) => async (values, actions) => {
    try {
      await axios.post(`${API.PHONE_VALIDATION}${this.getPhoneParams(values)}`);
    } catch (error) {
      actions.setErrors({ PhoneNumber: this.props.t(dict.INVALID_PHONE) });
      this.trackFormSubmitError(error);

      return;
    }

    try {
      const mappedValues = this.mapValues(values);
      const { data } = await axios.post(
        API.ENQUIRY,
        setAntiForgeryToken(this.enquiryFields.AntiForgery, mappedValues),
      );
      const { id } = data;

      this.setState({ isThankyouVisible: true });
      this.trackFormSubmitSuccess({
        formName,
        id,
      });
    } catch (error) {
      actions.setStatus({ msg: this.props.t(dict.GENERAL_ERROR) });
    }
  };

  renderFormStep() {
    const { PhoneNumberOptions, DomainCountryISOCode2 } = this.enquiryFields;
    const initialValues = {
      FullName: '',
      Email: '',
      PhoneNumber: '',
      PrivacyPolicy: true,
    };
    const { t } = this.props;

    const enquirySchema = object().shape({
      FullName: string().required(t(dict.REQUIRED_FIELD)),
      Email: string()
        .email(t(dict.INVALID_EMAIL))
        .required(t(dict.REQUIRED_FIELD)),
      PhoneNumber: string()
        .required(t(dict.REQUIRED_FIELD))
        .matches(REG_ONLY_NUMBERS, t(dict.INVALID_PHONE)),
    });

    return (
      <React.Fragment>
        {this.renderFormHeadline()}
        <Formik
          initialValues={initialValues}
          validationSchema={enquirySchema}
          onSubmit={this.onFormSubmit(this.props)}
          render={({
            errors,
            touched,
            handleChange,
            handleSubmit,
            status,
            values,
          }) => (
            <form id={FORM_ID} onSubmit={handleSubmit}>
              <Input
                css={styles.enquiryInput}
                {...styles.inputProps}
                id="FullName"
                placeholder={decorateRequiredPlaceholder(
                  t(dict.PLACEHOLDER_FULL_NAME),
                )}
                onChange={handleChange}
                error={errors.FullName && touched.FullName}
                errorMessage={errors.FullName}
                value={values.FullName}
              />
              <Input
                css={styles.enquiryInput}
                {...styles.inputProps}
                id="Email"
                placeholder={decorateRequiredPlaceholder(
                  t(dict.PLACEHOLDER_EMAIL),
                )}
                type="email"
                onChange={handleChange}
                error={errors.Email && touched.Email}
                errorMessage={errors.Email}
                value={values.Email}
              />
              <PhoneNumber
                options={PhoneNumberOptions}
                customCss={[styles.enquiryInput, styles.phoneNumberField]}
                innerCss={styles.phoneNumberFieldProps}
                id="PhoneNumber"
                ref={this.phoneNumberRef}
                defaultValue={DomainCountryISOCode2.value}
                placeholder={decorateRequiredPlaceholder(
                  t(dict.PLACEHOLDER_PHONE_NUMBER),
                )}
                onChange={handleChange}
                error={errors.PhoneNumber && touched.PhoneNumber}
                errorMessage={errors.PhoneNumber}
                value={values.PhoneNumber}
              />

              <div css={styles.checkboxWrapper}>
                {status && status.msg && this.renderGeneralError(status.msg)}
                <Checkbox
                  css={styles.checkbox}
                  id="SendMessages"
                  onChangeProp={handleChange}
                  label={t(dict.CHECKBOX_COMMUNICATION_OFFER)}
                  value={values.SendMessages}
                />
              </div>
              <input
                css={[createButtonCss(), styles.button, styles.submitButton]}
                type="submit"
                value={t(dict.CTA_DISCUSS_QUOTE)}
              />
            </form>
          )}
        />
        {this.renderPrivacyPolicyLinkLabel()}
        {this.renderPhoneNumberBlock()}
      </React.Fragment>
    );
  }

  renderThankyouStep() {
    const {
      SidePanelFormThankYouHeadline,
      SidePanelFormThankYouText,
    } = this.solutionBuilderResultsFields;
    const language = get(this.props.sitecoreContext, 'route.itemLanguage', '');

    return (
      <React.Fragment>
        <h1 css={[styles.headline, styles.thankyouHeadline]}>
          {SidePanelFormThankYouHeadline.value}
        </h1>
        <p css={styles.text}>{SidePanelFormThankYouText.value}</p>
        <button css={createButtonCss()} onClick={() => exitBuilder(language)}>
          {this.props.t(dict.CTA_THANK_YOU_CTA)}
        </button>
      </React.Fragment>
    );
  }

  renderPrivacyPolicyLinkLabel() {
    return (
      <React.Fragment>
        <span css={styles.privacyLabel}>
          {this.props.t(dict.CHECKBOX_PRIVACY_POLICY)}
        </span>
        <CtaLink
          cta={this.enquiryFields.PrivacyPolicyLink}
          css={styles.checkbox}
        />
      </React.Fragment>
    );
  }

  safeRender() {
    return (
      <div>
        {this.state.isThankyouVisible
          ? this.renderThankyouStep()
          : this.renderFormStep()}
      </div>
    );
  }
}

SidePanelEnquiryForm.displayName = 'SidePanelEnquiryForm';

SidePanelEnquiryForm.defaultProps = {
  solutionBuilderResultsFields: solutionBuilderDefaultData,
  enquiryFields: enquiryDefaultData,
  t: key => key,
  location: {
    LocationName: '',
    ProtonId: '',
  },
};

const stringPropShape = {
  value: PropTypes.string,
};

SidePanelEnquiryForm.propTypes = {
  enquiryFields: PropTypes.shape({
    AntiForgery: PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string,
    }),
    ContactIcon: PropTypes.shape({
      value: PropTypes.shape({}),
    }),
    ContactPhrasex: PropTypes.string,
    ContactPhrase: PropTypes.shape({
      value: PropTypes.string,
    }),
    DomainCountryISOCode2: PropTypes.shape(stringPropShape),
    LocalPhoneNumber: PropTypes.shape(stringPropShape),
    PhoneNumberOptions: PropTypes.array,
    PrivacyPolicyLink: PropTypes.object,
  }),
  solutionBuilderResultsFields: PropTypes.shape({
    SidePanelFormHeadline: PropTypes.shape(stringPropShape),
    SidePanelFormThankYouHeadline: PropTypes.shape(stringPropShape),
    SidePanelFormThankYouText: PropTypes.shape(stringPropShape),
  }),
  t: PropTypes.func,
  location: PropTypes.shape({
    LocationName: PropTypes.string,
    ProtonId: PropTypes.string,
  }),
};

export default compose(
  withNamespaces(),
  withSitecoreContext(),
)(SidePanelEnquiryForm);
